import React from 'react';
import {isIOS, isMacOs} from 'react-device-detect';
import {StyleProp, View, ViewStyle} from 'react-native';
import {Platform} from 'react-native';
import {makeStyles} from '@rneui/themed';

import useStorePriceDisplayConditions from '../../../hooks/usePriceDisplayConditions';
import {ExternalSellType, getProductDisplayInfo} from '../../../hooks/useProductDisplayInfo';
import {OrderLine} from '../../../transport/apiServiceResponse';
import {ProductDisplayRules} from '../../../utils/rewards';
import Text from '../../themed/Text';
import {TILE_DIMENSIONS, TYPOGRAPHY} from '../styles';

type BaseProps = {
  containerStyle?: StyleProp<ViewStyle>;
  center?: boolean;
};

type NewApiProps = BaseProps & {
  item: OrderLine;
  externalSellType?: never;
  productDisplayRules?: never;
};

type LegacyApiProps = BaseProps & {
  item?: never;
  externalSellType?: ExternalSellType;
  productDisplayRules: ProductDisplayRules;
};

type SimplifiedProps = BaseProps & {
  item?: never;
  externalSellType?: never;
  productDisplayRules?: never;
  showLabel: boolean;
  labelText: 'MEMBERS PRICE' | 'JUST FOR YOU' | null;
};

type Props = NewApiProps | LegacyApiProps | SimplifiedProps;

function MembersPriceLabel(props: Props) {
  const {containerStyle, center} = props;
  const styles = useStyles({center});
  const {showMemberPrice} = useStorePriceDisplayConditions();

  // Derive values depending on which API is used
  let externalSellType: ExternalSellType;
  let showMemberPriceLabel: boolean;

  if ('showLabel' in props) {
    // Simplified API: caller already resolved pricing
    externalSellType =
      props.labelText === 'JUST FOR YOU' ? 'private' : props.labelText ? 'public' : undefined;
    showMemberPriceLabel = props.showLabel;
  } else if ('item' in props && props.item) {
    // New API: use getProductDisplayInfo (non-hook version)
    const displayInfo = getProductDisplayInfo(props.item, showMemberPrice);
    externalSellType = displayInfo.externalSellType;
    showMemberPriceLabel = displayInfo.showMemberPriceLabel;
  } else {
    // Legacy API: derive from props
    externalSellType = props.externalSellType;
    const productDisplayRules = props.productDisplayRules;
    // DIS-1590: advertising follows the store, not the shopper — see
    // getProductDisplayInfo.
    showMemberPriceLabel =
      (externalSellType === 'public' &&
        showMemberPrice.storeLoyaltyEnabled &&
        productDisplayRules.presentAsPublicMemberProduct) ||
      (externalSellType === 'private' &&
        showMemberPrice.privatePricing &&
        productDisplayRules.presentAsPrivateMemberProduct);
  }

  const MEMBER_TEXT = {
    public: 'MEMBERS PRICE',
    private: 'JUST FOR YOU',
  };

  const STYLES = {
    public: styles.containerPublic,
    private: styles.containerPrivate,
  };

  if (!externalSellType || !showMemberPriceLabel) {
    // If we return null, there will be vertical spacing issues
    return <View style={[styles.containerCommon, styles.noBackground]} />;
  }

  return (
    <View style={[styles.containerCommon, containerStyle, STYLES[externalSellType]]}>
      <Text style={styles.text}>{MEMBER_TEXT[externalSellType]}</Text>
    </View>
  );
}

export default MembersPriceLabel;

const useStyles = makeStyles((theme, props: {center?: boolean}) => ({
  containerCommon: {
    height: TILE_DIMENSIONS.memberLabelHeight,
    paddingHorizontal: TILE_DIMENSIONS.memberLabelPaddingH,
    justifyContent: 'center',
  },
  containerPublic: {
    backgroundColor: theme.colors.yellow,
  },
  containerPrivate: {
    borderWidth: TILE_DIMENSIONS.memberLabelBorderWidth,
    borderColor: theme.colors.yellow,
  },
  noBackground: {backgroundColor: 'initial'},
  text: {
    color: theme.colors?.legacy?.text?.heading?.primary,
    ...TYPOGRAPHY.memberLabel,
    ...Platform.select({
      ios: {
        transform: [{translateY: 1}],
      },
      web:
        isMacOs || isIOS
          ? {
              transform: [{translateY: 1}],
            }
          : {
              transform: [{translateY: -1}],
            },
    }),
    ...(props.center ? {textAlign: 'center'} : {}),
  },
}));
